/**
 * hundoRaidCatalogue — every Pokémon in raids right now, priced at 100%.
 *
 * The Profile's "Hundo Raid CPs" block (2026-09-04 owner ask) lists every
 * current raid boss, including the bosses of live and imminent events such
 * as GO Fest's Mega Finale, and for the one picked shows the 15/15/15 catch
 * CP at level 20 and at level 25 (weather boosted).
 *
 * TWO SOURCES, ONE LIST.
 *
 *   /api/events/raids   LeekDuck's current rotation, by tier. Each boss
 *                       arrives with LeekDuck's own 100% CPs, which win
 *                       whenever present: they are the community's checked
 *                       figures and a wrong hundo CP costs someone a Pokémon.
 *   /api/events/live    the event feed. An event's bosses arrive as bare
 *                       species ids: no tier, no Mega flag, no form. So the
 *                       EVENT NAME decides what the species is in that raid:
 *                       "Mega" makes every Mega form of the species a boss
 *                       (Mega Mewtwo X and Y both), "Armored" picks Mewtwo's
 *                       armored stats, "Shadow" only changes the name (a
 *                       Shadow's base stats are the species' own), and a
 *                       "(Origin Forme)" picks that form's stats.
 *
 * Where LeekDuck gives no figure the CP is COMPUTED from the stats of what
 * the raid actually YIELDS: a Mega raid hands over the base Pokémon, so a
 * Mega boss is priced on the base species (Mega Starmie's 1476 is Starmie's
 * hundo, and Mega Mewtwo X and Y catch the same Mewtwo), while its weather
 * follows the Mega form's own types, since that is what is boosted in the
 * raid. Regional and alternate forms come from the raid attackers table,
 * everything else from the species table. A boss whose form cannot be
 * resolved to stats shows no CP rather than a base-form guess.
 *
 * Pure: no fetching, no DOM. The block hands it the two responses.
 */
import { baseStatsForSpecies } from "./generated/speciesBaseStats.generated";
import { RAID_ATTACKERS } from "./generated/raidMeta.generated";
import { MEGA_STATS, megaStatFor } from "./generated/megaStats.generated";
import { megaNamePrefix, speciesHasMega } from "./megaLevels";
import { findRaidAttacker } from "./attackerForm";
import { computeCp, type BaseStats } from "./ivSolver";
import {
  RAID_TIERS_BIGGEST_FIRST,
  raidBossFormId,
  raidTierLabel,
  type RaidBossDto,
  type RaidTierKey,
} from "./raidRoster";
import type { RaidBossArtRef } from "./formSpriteCandidates";
import { speciesName } from "@/data/speciesNames";
import { eventEndMs, eventStartMs, type RecencyEvent } from "./eventRecency";
import { applyRaidDaySchedule, raidDayGroupLabel } from "./raidDaySchedule";

export const RAID_CATCH_LEVEL = 20;
export const RAID_CATCH_LEVEL_BOOSTED = 25;
const HUNDO_IV = { atk: 15, def: 15, sta: 15 };

/** How far ahead an upcoming event's bosses are listed (the Mega Finale case). */
export const UPCOMING_EVENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export interface HundoRaidEntry {
  /** Stable pick key. */
  key: string;
  /** "Mega Mewtwo Y", "Armored Mewtwo", "Shadow Snorlax". */
  name: string;
  speciesId: number;
  isMega: boolean;
  /** "" / "X" / "Y" for a Mega. */
  megaForm: string;
  /** Regional-form registry id ("darumaka-galar") when the roster gave one. */
  formId: string | null;
  /** Raid attackers form label ("A" for Armored Mewtwo, "Origin"), else null. */
  formLabel: string | null;
  isShadow: boolean;
  canBeShiny: boolean;
  /** Lowercase types, from the stats row that priced it (or the feed). */
  types: string[];
  /** Roster tier, or "event" for an event's boss. */
  tier: RaidTierKey | "event";
  /** Section heading the picker groups it under. */
  group: string;
  /** Event bosses: when the event starts, for the "from Saturday" note. */
  startsAt: string | null;
  hundoCp: { normal: number; boosted: number } | null;
  cpSource: "leekduck" | "computed" | null;
  art: RaidBossArtRef;
}

export interface RaidsResponseLike {
  tiers?: Partial<Record<string, RaidBossDto[]>>;
}

export interface LiveEventLike extends RecencyEvent {
  id: string;
  name: string;
  category?: string;
  rawType?: string;
  spawns?: Array<{ speciesId?: number; name?: string }>;
}

export interface LiveEventsResponseLike {
  active?: LiveEventLike[];
  upcoming?: LiveEventLike[];
}

/** CP of a 15/15/15 copy at a level. */
export function hundoCpAt(stats: BaseStats, level: number): number {
  return computeCp(stats, HUNDO_IV, level);
}

function statsToCp(stats: BaseStats | null): { normal: number; boosted: number } | null {
  if (!stats) return null;
  return {
    normal: hundoCpAt(stats, RAID_CATCH_LEVEL),
    boosted: hundoCpAt(stats, RAID_CATCH_LEVEL_BOOSTED),
  };
}

/** The stats row for a boss shape, or null when the form cannot be priced. */
export function bossBaseStats(entry: {
  speciesId: number;
  isMega: boolean;
  megaForm: string;
  formId: string | null;
  formLabel: string | null;
}): { stats: BaseStats; types: string[] } | null {
  if (entry.isMega) {
    // The catch is the BASE Pokémon; only the weather types are the Mega's.
    const base = baseStatsForSpecies(entry.speciesId);
    if (!base) return null;
    const m = megaStatFor(entry.speciesId, entry.megaForm);
    const row = RAID_ATTACKERS.find((a) => a.dexId === entry.speciesId && a.form == null);
    return { stats: base, types: m?.types ?? row?.types ?? [] };
  }
  if (entry.formLabel) {
    const row = RAID_ATTACKERS.find(
      (a) => a.dexId === entry.speciesId && a.form != null && a.form.toLowerCase() === entry.formLabel!.toLowerCase(),
    );
    return row ? { stats: { atk: row.atk, def: row.def, sta: row.sta }, types: row.types } : null;
  }
  if (entry.formId) {
    const row = findRaidAttacker(entry.speciesId, entry.formId);
    // findRaidAttacker falls back to the base row for an unknown form; a
    // regional priced as its Kanto shape would be exactly the wrong number.
    if (!row || row.form == null) return null;
    return { stats: { atk: row.atk, def: row.def, sta: row.sta }, types: row.types };
  }
  const base = baseStatsForSpecies(entry.speciesId);
  if (!base) return null;
  const row = RAID_ATTACKERS.find((a) => a.dexId === entry.speciesId && a.form == null);
  return { stats: base, types: row?.types ?? [] };
}

function megaFormFromName(name: string): string {
  const m = /\b([XY])$/.exec(name.trim());
  return m ? m[1] : "";
}

/** The roster's bosses, biggest tier first, LeekDuck's CP where it gave one. */
function rosterEntries(raids: RaidsResponseLike | undefined, now: number): HundoRaidEntry[] {
  const out: HundoRaidEntry[] = [];
  // Today's Mega bosses by the local clock when an event rotates them
  // (raidDaySchedule.ts); the feed's roster otherwise.
  const tiers = applyRaidDaySchedule(raids?.tiers ?? {}, now);
  const megaLabel = raidDayGroupLabel(now);
  for (const tier of RAID_TIERS_BIGGEST_FIRST) {
    for (const boss of tiers[tier] ?? []) {
      if (typeof boss.speciesId !== "number" || boss.speciesId < 1) continue;
      const isMega = boss.isMega || tier === "mega";
      const megaForm = isMega ? megaFormFromName(boss.name) : "";
      const formId = isMega ? null : raidBossFormId(boss);
      const shape = { speciesId: boss.speciesId, isMega, megaForm, formId, formLabel: null };
      const priced = boss.hundoCp ? null : bossBaseStats(shape);
      const feedTypes = (boss.types ?? []).map((t) => t.toLowerCase());
      out.push({
        key: `raids:${tier}:${boss.speciesId}:${megaForm}:${formId ?? ""}:${boss.isShadow ? "s" : ""}`,
        name: boss.name,
        speciesId: boss.speciesId,
        isMega,
        megaForm,
        formId,
        formLabel: null,
        isShadow: boss.isShadow,
        canBeShiny: boss.canBeShiny,
        types: feedTypes.length > 0 ? feedTypes : (priced?.types ?? bossBaseStats(shape)?.types ?? []),
        tier,
        group: tier === "mega" && megaLabel ? megaLabel : raidTierLabel(tier),
        startsAt: null,
        hundoCp: boss.hundoCp ?? statsToCp(priced?.stats ?? null),
        cpSource: boss.hundoCp ? "leekduck" : priced ? "computed" : null,
        art: { speciesId: boss.speciesId, isMega, name: boss.name, formId },
      });
    }
  }
  return out;
}

const RAID_EVENT_CATEGORIES = new Set(["raid", "raid-day", "raid-hour", "go-fest", "raid-battles"]);

/** Whether an event's bosses belong in the list at all. */
export function isRaidEvent(ev: LiveEventLike): boolean {
  if (ev.category && RAID_EVENT_CATEGORIES.has(ev.category)) return true;
  if (ev.rawType && RAID_EVENT_CATEGORIES.has(ev.rawType)) return true;
  return /\braids?\b/i.test(ev.name) && !/battle league|go battle/i.test(ev.name);
}

/** "(Origin Forme)" / "(Altered Forme)" in an event name, as the attackers label. */
function formLabelFromEventName(name: string): string | null {
  const m = /\(([A-Za-z ]+?)\s*Forme?\)/i.exec(name);
  if (!m) return null;
  const label = m[1].trim();
  // The base row already IS Altered / Incarnate / Ordinary.
  if (/^(altered|incarnate|ordinary)$/i.test(label)) return null;
  return label;
}

/** The forms catalogue's id for Armored Mewtwo (client/src/lib/generated/pokemonForms.generated.ts). */
export const ARMORED_MEWTWO_FORM_ID = "mewtwo-armored";

/** One event's bosses, shaped by what its name says about them. */
function eventEntries(ev: LiveEventLike, group: string): HundoRaidEntry[] {
  const out: HundoRaidEntry[] = [];
  const isMegaEvent = /\bmega\b/i.test(ev.name) && !/\bmega energy\b/i.test(ev.name);
  const isShadow = /\bshadow\b/i.test(ev.name);
  const armored = /\barmou?red\b/i.test(ev.name);
  const namedForm = formLabelFromEventName(ev.name);
  const seen = new Set<number>();
  for (const sp of ev.spawns ?? []) {
    const speciesId = sp.speciesId;
    if (typeof speciesId !== "number" || speciesId < 1 || seen.has(speciesId)) continue;
    seen.add(speciesId);
    const megaForms = isMegaEvent ? MEGA_STATS.filter((m) => m.dexId === speciesId) : [];
    if (megaForms.length > 0) {
      for (const m of megaForms) {
        const shape = { speciesId, isMega: true, megaForm: m.form, formId: null, formLabel: null };
        const priced = bossBaseStats(shape);
        out.push({
          key: `event:${ev.id}:${speciesId}:${m.form}`,
          name: m.name,
          speciesId,
          isMega: true,
          megaForm: m.form,
          formId: null,
          formLabel: null,
          isShadow: false,
          canBeShiny: false,
          types: priced?.types ?? m.types,
          tier: "event",
          group,
          startsAt: ev.start ?? null,
          hundoCp: statsToCp(priced?.stats ?? null),
          cpSource: priced ? "computed" : null,
          art: { speciesId, isMega: true, name: m.name, formId: null },
        });
      }
      continue;
    }
    if (isMegaEvent && speciesHasMega(speciesId)) {
      // A roster Mega the Game Master has not priced yet (Mega Staraptor ahead
      // of its Super Mega Raid Day, 2026-09-07): MEGA_STATS has no row, but the
      // catch is the base Pokémon either way, so the boss still gets its hundo
      // CP from the base stats and the Mega's name. Types come from the raid
      // roster's base row (the Mega's own typing arrives with its stats row).
      const name = `${megaNamePrefix(speciesId)} ${speciesName(speciesId)}`;
      const shape = { speciesId, isMega: true, megaForm: "", formId: null, formLabel: null };
      const priced = bossBaseStats(shape);
      out.push({
        key: `event:${ev.id}:${speciesId}:`,
        name,
        speciesId,
        isMega: true,
        megaForm: "",
        formId: null,
        formLabel: null,
        isShadow: false,
        canBeShiny: false,
        types: priced?.types ?? [],
        tier: "event",
        group,
        startsAt: ev.start ?? null,
        hundoCp: statsToCp(priced?.stats ?? null),
        cpSource: priced ? "computed" : null,
        art: { speciesId, isMega: true, name, formId: null },
      });
      continue;
    }
    const isArmored = armored && speciesId === 150;
    const formLabel = isArmored ? "A" : namedForm;
    const shape = { speciesId, isMega: false, megaForm: "", formId: null, formLabel };
    // Armored Mewtwo is a curated FORM in the forms catalogue (owner ask
    // 2026-09-05: the card drew plain Mewtwo), so its art resolves through
    // the form id while the price stays on the "A" attacker row above.
    const artFormId = isArmored ? ARMORED_MEWTWO_FORM_ID : null;
    const priced = bossBaseStats(shape);
    const base = speciesName(speciesId);
    const name =
      armored && speciesId === 150
        ? "Armored Mewtwo"
        : `${isShadow ? "Shadow " : ""}${base}${namedForm ? ` (${namedForm})` : ""}`;
    out.push({
      key: `event:${ev.id}:${speciesId}:${formLabel ?? ""}`,
      name,
      speciesId,
      isMega: false,
      megaForm: "",
      formId: null,
      formLabel,
      isShadow,
      canBeShiny: false,
      types: priced?.types ?? [],
      tier: "event",
      group,
      startsAt: ev.start ?? null,
      hundoCp: statsToCp(priced?.stats ?? null),
      cpSource: priced ? "computed" : null,
      art: { speciesId, isMega: false, name, formId: artFormId },
    });
  }
  return out;
}

/** Identity for de-duplication across the roster and the events. */
function identity(e: HundoRaidEntry): string {
  return `${e.speciesId}|${e.isMega ? `mega${e.megaForm}` : ""}|${e.formId ?? ""}|${e.formLabel ?? ""}|${e.isShadow ? "s" : ""}`;
}

/**
 * A raid event running longer than this is a standing rotation (a two-week
 * 5★ line-up), not the weekend's headline. Same threshold as the Calendar's
 * Today card (lib/todayFocus).
 */
export const HEADLINE_EVENT_MAX_DURATION_MS = 8 * 24 * 60 * 60 * 1000;

/**
 * The full list, in the order the picker shows it:
 *
 *   1. HEADLINE events: raid events running NOW by the device clock and no
 *      longer than a week (a raid weekend, a Raid Day), soonest to end first.
 *      Armored Mewtwo on GO Fest Saturday leads the list and is the default
 *      pick (owner ask 2026-09-06); the roster's standing Megas used to.
 *   2. The roster, biggest tier first.
 *   3. Longer live raid events (a two-week 5★ rotation), soonest to end first.
 *   4. Events starting within the window, soonest first.
 *
 * "Running now" is read from the event's own times, not the server's bucket:
 * the bucket lags an event that opened at 10 a.m. local. A boss the roster
 * already lists is not repeated under an event; a Mega both a Mega raid and
 * the Mega Finale feature appears once, with the first listing's figure.
 */
export function buildHundoRaidCatalogue(
  raids: RaidsResponseLike | undefined,
  live: LiveEventsResponseLike | undefined,
  now: number = Date.now(),
): HundoRaidEntry[] {
  const out: HundoRaidEntry[] = [];
  const seen = new Set<string>();
  const push = (e: HundoRaidEntry) => {
    const id = identity(e);
    if (seen.has(id)) return;
    seen.add(id);
    out.push(e);
  };
  const headline: Array<{ ev: LiveEventLike; endsAt: number }> = [];
  const standing: Array<{ ev: LiveEventLike; endsAt: number }> = [];
  const later: Array<{ ev: LiveEventLike; startsIn: number }> = [];
  const seenEvents = new Set<string>();
  for (const ev of [...(live?.active ?? []), ...(live?.upcoming ?? [])]) {
    if (!isRaidEvent(ev) || seenEvents.has(ev.id)) continue;
    seenEvents.add(ev.id);
    const start = eventStartMs(ev);
    const end = eventEndMs(ev);
    if (Number.isFinite(end) && end <= now) continue;
    if (Number.isFinite(start) && start <= now) {
      const short = Number.isFinite(end) && end - start <= HEADLINE_EVENT_MAX_DURATION_MS;
      (short ? headline : standing).push({ ev, endsAt: end });
      continue;
    }
    const startsIn = start - now;
    if (!Number.isFinite(startsIn) || startsIn > UPCOMING_EVENT_WINDOW_MS) continue;
    later.push({ ev, startsIn });
  }
  // A raid event names its boss (Armored Mewtwo in 5-star Raid Battles); a
  // fest lists a dozen Megas around it. The named boss leads, then soonest
  // to end.
  const fest = (ev: LiveEventLike) => (ev.category === "go-fest" || ev.rawType === "pokemon-go-fest" ? 1 : 0);
  headline.sort((a, b) => fest(a.ev) - fest(b.ev) || a.endsAt - b.endsAt);
  standing.sort((a, b) => a.endsAt - b.endsAt);
  later.sort((a, b) => a.startsIn - b.startsIn);

  for (const { ev } of headline) for (const e of eventEntries(ev, ev.name)) push(e);
  for (const e of rosterEntries(raids, now)) push(e);
  for (const { ev } of standing) for (const e of eventEntries(ev, ev.name)) push(e);
  for (const { ev } of later) for (const e of eventEntries(ev, ev.name)) push(e);
  return out;
}

/** The pick to open on: the biggest live boss, a Mega before a 5★. */
export function defaultHundoPick(entries: readonly HundoRaidEntry[]): HundoRaidEntry | null {
  return entries.find((e) => e.hundoCp != null) ?? entries[0] ?? null;
}
